// Shared constants and pure helpers for the kennenlernen inline lead funnel.
// Submit payload still mirrors RegisterForm; keep both in sync when the
// register contract changes.
import { calcAge } from '../signup/helpers';
import { INTENTS } from '../signup/signupConfig';

export const TOTAL_STEPS = 6;
export const AGE_SLIDER_MIN = 18;
export const AGE_SLIDER_MAX = 99;
export const AGE_DEFAULT_MAX = 66;

export const STEP_TITLES = [
  'Wen möchtest du kennenlernen?',
  'Im Alter von … bis …',
  'Wonach suchst du gerade?',
  'Wann hast du Geburtstag?',
  'Wo bist du zu Hause?',
  'Fast geschafft.',
];

export const LOADER_MSGS = [
  'Es schlägt für dich – dein Profil wird erstellt…',
  'Online-Profile werden für dich gesucht…',
  'Passende Chats werden für dich vorbereitet…',
  'Gleich kann es losgehen – fast fertig…',
];

export const DRAFT_KEY = 'viluu_signup_draft_v1';
export const DRAFT_TTL = 30 * 24 * 60 * 60 * 1000;

export const FALLBACK_LOCATIONS = [
  { id: 1, zip_code: '10115', city: 'Berlin', region: 'Berlin', country_code: 'de' },
  { id: 2, zip_code: '1010', city: 'Wien', region: 'Wien', country_code: 'at' },
  { id: 3, zip_code: '8001', city: 'Zürich', region: 'Zürich', country_code: 'ch' },
];

export const FUNNEL_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const DISPOSABLE = new Set([
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.info',
  'guerrillamail.biz',
  'guerrillamail.net',
  'guerrillamail.org',
  'guerrillamailblock.com',
  'sharklasers.com',
  'grr.la',
  'spam4.me',
  'pokemail.net',
  '10minutemail.com',
  '10minutemail.net',
  '20minutemail.com',
  'tempmail.com',
  'temp-mail.org',
  'temp-mail.io',
  'temp-mail.ru',
  'tempmail.net',
  'tempmailo.com',
  'tmpmail.org',
  'tmpmail.net',
  'throwawaymail.com',
  'throwawaymail.net',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'trashmail.com',
  'trashmail.de',
  'trashmail.net',
  'trash-mail.com',
  'trash-mail.de',
  'wegwerfmail.de',
  'wegwerfmail.net',
  'wegwerfmail.org',
  'wegwerfemail.de',
  'einrottenmail.de',
  'einmalmail.de',
  'kurzepost.de',
  'squizzy.de',
  'byom.de',
  'mailnesia.com',
  'maildrop.cc',
  'getnada.com',
  'nada.email',
  'dispostable.com',
  'fakeinbox.com',
  'fakemail.net',
  'mintemail.com',
  'mohmal.com',
  'spamgourmet.com',
  'mytemp.email',
  'emailondeck.com',
  'getairmail.com',
  'mailcatch.com',
  'tempinbox.com',
  'discard.email',
  'discardmail.com',
  'jetable.org',
  'moakt.com',
  'tafmail.com',
  'cs.email',
  'inboxbear.com',
  '33mail.com',
  'anonbox.net',
  'spambog.com',
  'mailnull.com',
  'harakirimail.com',
  'mailimate.com',
  'spam.la',
  'tempr.email',
  'tempmailaddress.com',
  'luxusmail.org',
  'mailtemp.net',
  'burnermail.io',
  'mailpoof.com',
  'tempemail.net',
  'minuteinbox.com',
  '1secmail.com',
  '1secmail.org',
  '1secmail.net',
  'fakemailgenerator.com',
  'mailde.de',
  'mailde.info',
  'dropmail.me',
]);

const POPULAR = [
  'gmail.com',
  'googlemail.com',
  'web.de',
  'gmx.de',
  'gmx.net',
  'gmx.at',
  'gmx.ch',
  't-online.de',
  'hotmail.com',
  'hotmail.de',
  'outlook.com',
  'outlook.de',
  'live.de',
  'live.com',
  'yahoo.com',
  'yahoo.de',
  'icloud.com',
  'me.com',
  'aol.com',
  'freenet.de',
  'mail.de',
  'posteo.de',
  'mailbox.org',
  'proton.me',
  'protonmail.com',
];

export function emptyForm() {
  return {
    day: '',
    month: '',
    year: '',
    locationId: null,
    locationLabel: '',
    gender: null,
    preferredGender: [],
    ageMin: AGE_SLIDER_MIN,
    ageMax: AGE_DEFAULT_MAX,
    preferences: [],
    name: '',
    email: '',
    password: '',
    terms: false,
  };
}

export function isFunnelEmail(s) {
  return FUNNEL_EMAIL_RE.test(s || '');
}

function emailDomain(s) {
  const p = String(s || '')
    .toLowerCase()
    .trim()
    .split('@');
  return p.length === 2 ? p[1] : '';
}

export function isDisposable(s) {
  return DISPOSABLE.has(emailDomain(s));
}

function lev(a, b) {
  const m = a.length;
  const n = b.length;
  let prev = [];
  let cur = [];
  for (let j = 0; j <= n; j++) {
    prev[j] = j;
  }
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur.slice();
  }
  return prev[n];
}

export function suggestEmail(s) {
  const v = String(s || '')
    .toLowerCase()
    .trim();
  const at = v.lastIndexOf('@');
  if (at < 1) {
    return null;
  }
  const local = v.slice(0, at);
  const dom = v.slice(at + 1);
  if (!local || dom.length < 4) {
    return null;
  }
  if (POPULAR.indexOf(dom) !== -1 || DISPOSABLE.has(dom)) {
    return null;
  }
  let best = null;
  let bestD = 99;
  for (let i = 0; i < POPULAR.length; i++) {
    const d = lev(dom, POPULAR[i]);
    if (d < bestD) {
      bestD = d;
      best = POPULAR[i];
    }
  }
  return best && bestD >= 1 && bestD <= 2 ? local + '@' + best : null;
}

// Match the main landing signup form: only an 8-character minimum.
export function isPasswordOk(password) {
  return (password || '').length >= 8;
}

export function formAge(form) {
  return calcAge(form.day, form.month, form.year);
}

export function isStepValid(n, form) {
  switch (n) {
    case 1:
      return !!form.gender && form.preferredGender.length > 0;
    case 2:
      return true;
    case 3:
      return form.preferences.length > 0;
    case 4: {
      const a = formAge(form);
      return a !== null && a >= 18 && a <= 120;
    }
    case 5:
      return !!form.locationId;
    case 6:
      return (
        (form.name || '').trim().length >= 2 &&
        isFunnelEmail(form.email) &&
        !isDisposable(form.email) &&
        isPasswordOk(form.password) &&
        form.terms === true
      );
    default:
      return false;
  }
}

export function crumbFor(step, form, trans) {
  switch (step) {
    case 1: {
      if (!form.gender) {
        return null;
      }
      const g = form.gender === 'male' ? 'Mann' : 'Frau';
      const p =
        form.preferredGender.length === 2
          ? 'sucht Alle'
          : form.preferredGender[0] === 'female'
            ? 'sucht Frauen'
            : 'sucht Männer';
      return g + ' · ' + p;
    }
    case 2:
      return form.ageMin + '–' + form.ageMax;
    case 3: {
      if (!form.preferences.length) {
        return null;
      }
      const labelKey = INTENTS.find((el) => el.id === form.preferences[0]).labelKey;
      const first = trans.get('landing:signup.' + labelKey);

      return form.preferences.length > 1 ? first + ' +' + (form.preferences.length - 1) : first;
    }
    case 4: {
      const a = formAge(form);
      return a !== null && a >= 18 ? a + ' J.' : null;
    }
    case 5:
      return form.locationId ? form.locationLabel : null;
    default:
      return null;
  }
}

export function funnelAgeHint(form) {
  const dStr = form.day || '';
  const mStr = form.month || '';
  const yStr = form.year || '';
  const d = +form.day;
  const m = +form.month;
  const y = +form.year;

  if (!dStr && !mStr && !yStr) {
    return { text: 'Bitte gib dein Geburtsdatum ein', ok: false, bad: false, error: false };
  }
  if (dStr && (d < 1 || d > 31)) {
    return { text: 'Tag muss zwischen 1 und 31 liegen.', ok: false, bad: true, error: false };
  }
  if (mStr && (m < 1 || m > 12)) {
    return { text: 'Monat muss zwischen 1 und 12 liegen.', ok: false, bad: true, error: false };
  }
  if (yStr.length === 4) {
    const currentYear = new Date().getFullYear();
    if (y < 1900 || y > currentYear) {
      return {
        text: 'Jahr muss zwischen 1900 und ' + currentYear + ' liegen.',
        ok: false,
        bad: true,
        error: false,
      };
    }
  }
  if (dStr && mStr && yStr.length === 4) {
    const daysInMonth = new Date(y, m, 0).getDate();
    if (d > daysInMonth) {
      return {
        text: 'Der ' + m + '. Monat hat nur ' + daysInMonth + ' Tage.',
        ok: false,
        bad: true,
        error: false,
      };
    }
  }
  if (!dStr || !mStr || yStr.length !== 4) {
    return { text: 'Bitte gib Tag, Monat und Jahr ein.', ok: false, bad: false, error: false };
  }
  const a = formAge(form);
  if (a === null) {
    return { text: 'Bitte gib ein gültiges Datum ein.', ok: false, bad: true, error: false };
  }
  if (a < 18) {
    return {
      text: 'Du musst mindestens 18 sein (du bist ' + a + ').',
      ok: false,
      bad: true,
      error: true,
    };
  }
  if (a > 99) {
    return { text: 'Bitte gib ein gültiges Datum ein.', ok: false, bad: true, error: false };
  }
  return { text: 'Du bist ' + a + ' — perfekt, weiter geht’s.', ok: true, bad: false, error: false };
}

export function filterLocations(locations, q) {
  const Q = q.toLowerCase().trim();
  return locations
    .filter(
      (l) =>
        l.city.toLowerCase().includes(Q) ||
        String(l.zip_code).startsWith(q),
    )
    .slice(0, 8);
}

export function pad2(n) {
  n = String(n);
  return n.length === 1 ? '0' + n : n;
}

export function consentAllowsDraft() {
  try {
    const raw = localStorage.getItem('viluu_consent_v1');
    if (!raw) {
      return false;
    }
    return !!JSON.parse(raw).functional;
  } catch (e) {
    return false;
  }
}

export function homeUrl() {
  try {
    return zRoute('home');
  } catch (e) {
    return '/';
  }
}

export function firstIncompleteStep(form) {
  let target = 1;
  for (let s = 1; s <= TOTAL_STEPS; s++) {
    if (!isStepValid(s, form)) {
      target = s;
      break;
    }
    target = Math.min(s + 1, TOTAL_STEPS);
  }
  return target;
}
